import * as React from 'react';
import loadable from '@loadable/component';
import { useDispatch, useSelector } from 'react-redux';
import { ModalWindow } from '@cian/ui-kit/modal';
import { deleteCookie } from '@cian/utils/lib/browser/cookie';
import { GeoSwitcher } from '../../components/GeoSwitcher';
import { LoadableErrorBoundary } from '../../components/LoadableErrorBoundary';
import { LoadableFallback } from '../../components/LoadableFallback';
import { IApplicationState, TThunkDispatch } from '../../types/redux';
import { IGeoSwitcherState } from '../../types/geoSwitcher';
import { getRegionIdFromCookie, setRegionCookies } from './utils/cookie';
import { buildRedirectUrl } from './utils/url';
import { getPopularCities, getRegion } from '../../actions/geoSwitcher';
import { selectSubdomainCityId } from '../../selectors/selectSubdomainCityId';
import { selectSubdomainRegionId } from '../../selectors/selectSubdomainRegionId';
import { useApplicationContext } from '../../utils/applicationContext';
import { DEFAULT_SUBDOMAIN } from '../../constants/default';
import { DETECTED_REGION_ID_COOKIE_KEY } from '../../constants/geoSwitcher';

const GeoSwitcherContainerInnerLoadable = loadable(
  () => import(/* webpackChunkName: "GeoSwitcherContainerInnerLoadable" */ './GeoSwitcherContainer'),
  {
    ssr: false,
    fallback: <LoadableFallback />,
  },
);

export const GeoSwitcherWrapperContainer = () => {
  const dispatch: TThunkDispatch = useDispatch();
  const { custom } = useApplicationContext();
  const [open, setOpen] = React.useState(false);
  const { region, popularCities } = useSelector<IApplicationState, IGeoSwitcherState>(state => state.geoSwitcher);
  const subdomainCityId = useSelector(selectSubdomainCityId);
  const subdomainRegionId = useSelector(selectSubdomainRegionId);
  const currentRegionId = region?.id;
  const currentRegionIdRef = React.useRef<number | undefined>(currentRegionId);
  const hasSyncedSubdomainCookiesRef = React.useRef(false);
  currentRegionIdRef.current = currentRegionId;

  const handleOpenGeoSwitcher = React.useCallback(() => {
    setOpen(true);
  }, []);

  const handleCloseGeoSwitcher = React.useCallback(() => {
    setOpen(false);
  }, []);

  const retryLoadContent = React.useCallback(() => {
    GeoSwitcherContainerInnerLoadable.load();
  }, []);

  React.useEffect(() => {
    dispatch(getPopularCities());
  }, [dispatch]);

  React.useEffect(() => {
    const regionIdFromCookie = getRegionIdFromCookie();
    const isRegionalSubdomain = custom.subdomain !== DEFAULT_SUBDOMAIN;
    const regionId = isRegionalSubdomain
      ? subdomainCityId || subdomainRegionId || regionIdFromCookie || 1
      : regionIdFromCookie || subdomainCityId || subdomainRegionId || 1;

    if (currentRegionIdRef.current === regionId) {
      return;
    }

    dispatch(getRegion(regionId));
  }, [custom.subdomain, dispatch, subdomainCityId, subdomainRegionId]);

  React.useEffect(() => {
    if (!region || custom.subdomain === DEFAULT_SUBDOMAIN || hasSyncedSubdomainCookiesRef.current) {
      return;
    }

    setRegionCookies(region);
    deleteCookie(DETECTED_REGION_ID_COOKIE_KEY, { domain: '.cian.ru' });
    hasSyncedSubdomainCookiesRef.current = true;
  }, [custom.subdomain, region]);

  React.useEffect(() => {
    if (!region?.baseHost) {
      return;
    }

    if (!/^https?:\/\//.test(region.baseHost)) {
      return;
    }

    if (/localhost|127\.0\.0\.1/.test(window.location.origin)) {
      return;
    }

    const regionIdFromCookie = getRegionIdFromCookie();
    if (regionIdFromCookie) {
      return;
    }

    if (region.baseHost !== window.location.origin) {
      window.location.href = buildRedirectUrl(region.baseHost);
    }
  }, [region]);

  if (popularCities.length === 0 || !region) {
    return null;
  }

  return (
    <>
      <GeoSwitcher title={region.displayName} onClick={handleOpenGeoSwitcher} />
      <ModalWindow
        open={open}
        onClose={handleCloseGeoSwitcher}
        header={null}
        content={
          <LoadableErrorBoundary onRetry={retryLoadContent}>
            <GeoSwitcherContainerInnerLoadable closeGeoSuggest={handleCloseGeoSwitcher} />
          </LoadableErrorBoundary>
        }
        width={1179}
        size="M"
        fixed={false}
      />
    </>
  );
};
